/**
 * @file   src\components\EmployeeCard.tsx
 * @brief  Employee details card.
 * @date   Aug, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import DateTimePicker from 'react-datetime-picker';
import '../styles/ShiftCard.scss';
import { Accordion, Card, Col, Row, Button, useAccordionButton, useNavigate, useLocation, useState, useEffect, moment } from './ThirdPartyComponents';
import { CustomToggleProps, IEmployeeCard, IObject } from '../interfaces/generalInterface';
import KKICheckbox from './KKICheckbox';
import Arrow from '../assests/icons/DownArrow';
import Time from '../assests/icons/Time';
import useAccordionCollapse from '../hooks/useAccordionCollapse';
import {
  DATE_FORMAT_YYYY_MM_DD_HH_MM_SS,
  DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z,
  NUMBER_0,
  NUMBER_1,
  NUMBER_2,
  NUMBER_300,
  NUMBER_3,
  NUMBER_4,
  NUMBER_40,
  STORAGE_USER,
} from '../utils/constants';
import { ColorGroups, MyScheduleActions, NoShow, AdminRoutePath, NavigationToEmployeeCardFrom, CAListTypes, BreakTimeTypes, OperationTypes } from '../utils/enums';
import KKISelect from './KKISelect';
import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';
import {
  calculateHourDifference,
  calculateHoursBasedonMinutes,
  convertToDbTime,
  formatPhoneNumber,
  getDataFromStorage,
  getKeyIndicators,
  isCurrentTimeIsBeforeShiftStartTime,
} from '../utils/helper';
import { CHECKIN_TIME_MUST_BEFORE_CHECKOUT_TIME, CHECKOUT_TIME_MUST_AFTER_CHECKIN_TIME } from '../messages/validationMessages';
import KKIInput from './KKIInput';
import { ICADetails } from '../interfaces/caInterface';
import Strings from '../assests/strings/Strings.json';

const CustomToggle = ({ children, eventKey, callback }: CustomToggleProps) => {
  const decoratedOnClick = useAccordionButton(eventKey, () => {
    callback(eventKey);
  });

  return (
    <button type="button" className="accordion-btn" onClick={decoratedOnClick}>
      {children}
    </button>
  );
};

// NoShow status options
const show = [
  { label: NoShow[NoShow.Select].replace(/_/g, ' '), value: NoShow.Select.toString() },
  { label: NoShow[NoShow.Show].replace(/_/g, ' '), value: NoShow.Show.toString() },
  { label: NoShow[NoShow.No_Show].replace(/_/g, ' '), value: NoShow.No_Show.toString() },
  { label: NoShow[NoShow.Left_Early].replace(/_/g, ' '), value: NoShow.Left_Early.toString() },
  { label: NoShow[NoShow.Show_Late].replace(/_/g, ' '), value: NoShow.Show_Late.toString() },
];

// Break time status options
const breakTimeOptions = [
  { label: BreakTimeTypes[BreakTimeTypes.time_0_minute].replace('time_', '').replace(/_/g, ' '), value: BreakTimeTypes.time_0_minute.toString() },
  { label: BreakTimeTypes[BreakTimeTypes.time_15_minutes].replace('time_', '').replace(/_/g, ' '), value: BreakTimeTypes.time_15_minutes.toString() },
  { label: BreakTimeTypes[BreakTimeTypes.time_30_minutes].replace('time_', '').replace(/_/g, ' '), value: BreakTimeTypes.time_30_minutes.toString() },
  { label: BreakTimeTypes[BreakTimeTypes.time_45_minutes].replace('time_', '').replace(/_/g, ' '), value: BreakTimeTypes.time_45_minutes.toString() },
  { label: BreakTimeTypes[BreakTimeTypes.time_60_minutes].replace('time_', '').replace(/_/g, ' '), value: BreakTimeTypes.time_60_minutes.toString() },
];

const EmployeeCard = ({
  caDetails,
  shiftDetails,
  isFrom, // 1= from details page, 2= from assign page, 3 = callout page
  preferredType,
  onCheckboxChange,
  onButtonClick,
  onNoShowChange,
  onOnCallChange,
  selectedEmployees,
  isSelectAllChecked,
  onOnCallReplace,
  onUndoReplace,
}: IEmployeeCard) => {
  // Navigate object creation.
  const navigate = useNavigate();

  // Create location object to access location info.
  const location = useLocation();

  // Get the collapsed state using the custom hook
  const { isCollapsed, toggleCollapse } = useAccordionCollapse();

  // Access userInfo from the storage.
  const userInfo = getDataFromStorage(STORAGE_USER);
  // Loggined user role
  const roleId = userInfo?.roleId;

  // Initialize component stat variables.
  const [selectedShow, setSelectedShow] = useState<IObject | null>(null);
  const [onCallValues, setOnCallValues] = useState<boolean>(false);
  const [checkInTime, setCheckInTime] = useState<Date | null>(null);
  const [checkOutTime, setCheckOutTime] = useState<Date | null>(null);
  const [checkInErrorMessage, setCheckInErrorMessage] = useState<string>('');
  const [checkOutErrorMessage, setCheckOutErrorMessage] = useState<string>('');
  const [attempt1, setAttempt1] = useState<Date | null>(null);
  const [attempt2, setAttempt2] = useState<Date | null>(null);
  const [note, setNote] = useState<string>('');
  const [selectedBreakTime, setSelectedBreakTime] = useState<IObject | null>({
    label: BreakTimeTypes[BreakTimeTypes.time_0_minute].replace('time_', '').replace(/_/g, ' '),
    value: BreakTimeTypes.time_0_minute.toString(),
  });

  // Method to get checkin and checkout time.
  const getCheckinAndCheckoutTime = (caDetail: ICADetails) => {
    let parsedCheckInTime = null;
    let parsedCheckOutTime = null;
    if (caDetail?.checkInTime) {
      parsedCheckInTime = convertToDbTime(caDetail?.checkInTime);
    } else if (shiftDetails?.shiftFromTime) {
      parsedCheckInTime = convertToDbTime(shiftDetails?.shiftFromTime);
    }
    if (caDetail?.checkOutTime) {
      parsedCheckOutTime = convertToDbTime(caDetail?.checkOutTime);
    } else if (shiftDetails?.shiftToTime) {
      parsedCheckOutTime = convertToDbTime(shiftDetails?.shiftToTime);
    }
    return { parsedCheckInTime, parsedCheckOutTime };
  };

  // Setting the initial NoShow value based on the employee's details.
  useEffect(() => {
    const selectedShowValue = show.find((option) => option.value === caDetails?.isNoShow?.toString()) || null;
    setSelectedShow(selectedShowValue);
    setOnCallValues(caDetails?.isOnCall || false);
    const { parsedCheckInTime, parsedCheckOutTime } = getCheckinAndCheckoutTime(caDetails);
    setCheckInTime(parsedCheckInTime);
    setCheckOutTime(parsedCheckOutTime);
    if (caDetails?.attemptOne) {
      const parsedAttempt1 = convertToDbTime(caDetails?.attemptOne);
      setAttempt1(parsedAttempt1);
    }
    if (caDetails?.attemptTwo) {
      const parsedAttemp2 = convertToDbTime(caDetails?.attemptTwo);
      setAttempt2(parsedAttemp2);
    }
    setNote(caDetails?.note || '');
    const selectedBreakOption = breakTimeOptions.find((option) => option.value === caDetails?.breakTime?.toString()) || null;
    setSelectedBreakTime(selectedBreakOption);
    setCheckInErrorMessage('');
    setCheckOutErrorMessage('');
  }, [caDetails]);

  // Handle checkbox change event and notify the parent component.
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onCheckboxChange) {
      onCheckboxChange(e.target.checked, caDetails);
    }
  };

  // Method to handle checkin and out time.
  const handleCheckInOutTimes = (noShowStatus: number) => {
    const shouldUseCADetails = caDetails?.checkInTime && noShowStatus === caDetails?.isNoShow;
    let checkInTimeToSet = null;
    if (shouldUseCADetails) {
      checkInTimeToSet = convertToDbTime(caDetails.checkInTime || '');
    } else if (shiftDetails?.shiftFromTime) {
      checkInTimeToSet = convertToDbTime(shiftDetails.shiftFromTime);
    }
    setCheckInTime(checkInTimeToSet);

    let checkOutTimeToSet = null;
    if (shouldUseCADetails) {
      checkOutTimeToSet = convertToDbTime(caDetails.checkOutTime || '');
    } else if (shiftDetails?.shiftToTime) {
      checkOutTimeToSet = convertToDbTime(shiftDetails.shiftToTime);
    }
    setCheckOutTime(checkOutTimeToSet);
    return { checkInTimeToSet, checkOutTimeToSet };
  };

  // Method to handle no show attempts.
  const handleAttemptTimes = () => {
    setCheckInErrorMessage('');
    setCheckInErrorMessage('');
    setAttempt1(caDetails?.attemptOne ? convertToDbTime(caDetails.attemptOne) : null);
    setAttempt2(caDetails?.attemptTwo ? convertToDbTime(caDetails.attemptTwo) : null);
  };

  // Method to trigger no show card changes.
  const triggerNoShowChange = (noShowStatus: number, checkInDateTime: Date | null, checkOutDateTime: Date | null) => {
    const hourDiff = calculateHourDifference(checkInDateTime, checkOutDateTime);
    const caWorkHours = calculateHoursBasedonMinutes(Number(hourDiff), Number(selectedBreakTime?.value), OperationTypes.Subtract);
    onNoShowChange?.({
      caId: Number(caDetails.caId),
      noShowStatus,
      noShowHour: noShowStatus !== NoShow.No_Show ? caWorkHours.toString() || '' : NUMBER_0.toString(),
      checkInTime: noShowStatus !== NoShow.No_Show ? moment(checkInDateTime).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
      checkOutTime: noShowStatus !== NoShow.No_Show ? moment(checkOutDateTime).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
      attemptOne: noShowStatus === NoShow.No_Show && attempt1 ? moment(attempt1).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
      attemptTwo: noShowStatus === NoShow.No_Show && attempt2 ? moment(attempt2).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
      breakTime: selectedBreakTime?.value ? Number(selectedBreakTime?.value) : NUMBER_0,
      note,
      error: !!checkInErrorMessage || !!checkOutErrorMessage,
    });
  };

  // NoShow status change event hander.
  const handleNoShowStatusChange = (val: IObject) => {
    let checkInDateTime = checkInTime;
    let checkOutDateTime = checkOutTime;
    const noShowStatus = val.value;
    const selectedOption = show.find((option) => option.value === noShowStatus?.toString()) || null;
    setSelectedShow(selectedOption);
    if (Number(noShowStatus) !== NoShow.No_Show) {
      const { checkInTimeToSet, checkOutTimeToSet } = handleCheckInOutTimes(Number(noShowStatus));
      checkInDateTime = checkInTimeToSet;
      checkOutDateTime = checkOutTimeToSet;
    } else {
      handleAttemptTimes();
    }
    const breakTimeValue = caDetails?.breakTime && Number(noShowStatus) === caDetails?.isNoShow ? caDetails?.breakTime?.toString() : shiftDetails?.breakTime?.toString() || '';
    const selectedBreakOption = breakTimeOptions.find((option) => option.value === breakTimeValue) || null;
    setSelectedBreakTime(selectedBreakOption);
    setNote(caDetails?.note && Number(noShowStatus) === caDetails?.isNoShow ? caDetails?.note : '');
    setCheckInErrorMessage('');
    setCheckOutErrorMessage('');
    if (caDetails?.caId && onNoShowChange) {
      triggerNoShowChange(Number(noShowStatus), checkInDateTime, checkOutDateTime);
    }
  };

  // Methid to handle break time change.
  const handleBreakTimeChange = (val: IObject) => {
    setSelectedBreakTime(val);
    if (caDetails?.caId && selectedShow && onNoShowChange) {
      const hourDiff = calculateHourDifference(checkInTime, checkOutTime);
      const caWorkHours = calculateHoursBasedonMinutes(Number(hourDiff), Number(val?.value), OperationTypes.Subtract);
      onNoShowChange?.({
        caId: Number(caDetails.caId),
        noShowStatus: Number(selectedShow.value),
        noShowHour: caWorkHours.toString(),
        checkInTime: Number(selectedShow?.value) !== NoShow.No_Show ? moment(checkInTime).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
        checkOutTime: Number(selectedShow?.value) !== NoShow.No_Show ? moment(checkOutTime).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
        attemptOne: Number(selectedShow?.value) === NoShow.No_Show ? moment(attempt1).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
        attemptTwo: Number(selectedShow?.value) === NoShow.No_Show ? moment(attempt2).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
        breakTime: val?.value ? Number(val?.value) : NUMBER_0,
        note,
        error: !!checkInErrorMessage || !!checkOutErrorMessage,
      });
    }
  };

  // OnCall checkbox change event handler
  const handleOnCallChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOnCallValues(e.target.checked);
    if (caDetails?.caId && onOnCallChange) {
      onOnCallChange(Number(caDetails?.caId), e.target.checked, Number(caDetails?.colorGroup));
    }
  };

  // Handle check-in time change
  const handleCheckInChange = (value: Date | null) => {
    let err = false;
    if (value) {
      setCheckInErrorMessage('');
      const enteredTime = moment(value).format(DATE_FORMAT_YYYY_MM_DD_HH_MM_SS);
      if (checkOutTime && moment(enteredTime).isAfter(moment(checkOutTime))) {
        setCheckInErrorMessage(CHECKIN_TIME_MUST_BEFORE_CHECKOUT_TIME);
        setCheckOutErrorMessage('');
        err = true;
      } else {
        setCheckInErrorMessage('');
        setCheckOutErrorMessage('');
      }
      setCheckInTime(value);
    } else {
      setCheckInTime(null);
    }
    if (caDetails?.caId && selectedShow && onNoShowChange) {
      const hourDiff = calculateHourDifference(value, checkOutTime);
      const caWorkHours = calculateHoursBasedonMinutes(Number(hourDiff), Number(selectedBreakTime?.value), OperationTypes.Subtract);
      onNoShowChange?.({
        caId: Number(caDetails.caId),
        noShowStatus: Number(selectedShow.value),
        noShowHour: caWorkHours.toString(),
        checkInTime: moment(value).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z),
        checkOutTime: moment(checkOutTime).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z),
        attemptOne: null,
        attemptTwo: null,
        breakTime: selectedBreakTime?.value ? Number(selectedBreakTime?.value) : NUMBER_0,
        note,
        error: err,
      });
    }
  };

  // Handle check-out time change
  const handleCheckOutChange = (value: Date | null) => {
    let err = false;
    if (value) {
      setCheckOutErrorMessage('');
      const enteredTime = moment(value).format(DATE_FORMAT_YYYY_MM_DD_HH_MM_SS);
      if (checkInTime && moment(enteredTime).isBefore(moment(checkInTime))) {
        setCheckOutErrorMessage(CHECKOUT_TIME_MUST_AFTER_CHECKIN_TIME);
        setCheckInErrorMessage('');
        err = true;
      } else {
        setCheckOutErrorMessage('');
        setCheckInErrorMessage('');
      }
      setCheckOutTime(value);
    } else {
      setCheckOutTime(null);
    }
    if (caDetails?.caId && selectedShow && onNoShowChange) {
      const hourDiff = calculateHourDifference(checkInTime, value);
      const caWorkHours = calculateHoursBasedonMinutes(Number(hourDiff), Number(selectedBreakTime?.value), OperationTypes.Subtract);
      onNoShowChange?.({
        caId: Number(caDetails.caId),
        noShowStatus: Number(selectedShow.value),
        noShowHour: caWorkHours.toString(),
        checkInTime: moment(checkInTime).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z),
        checkOutTime: moment(value).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z),
        attemptOne: null,
        attemptTwo: null,
        breakTime: selectedBreakTime?.value ? Number(selectedBreakTime?.value) : NUMBER_0,
        note,
        error: err,
      });
    }
  };

  // Handle no show contact attempt1 change handler.
  const handleNoShowAttempt1Change = (value: Date | null) => {
    if (value) {
      setAttempt1(value);
    }
    if (caDetails?.caId && selectedShow && onNoShowChange) {
      onNoShowChange?.({
        caId: Number(caDetails.caId),
        noShowStatus: Number(selectedShow.value),
        noShowHour: NUMBER_0.toString(),
        checkInTime: null,
        checkOutTime: null,
        attemptOne: moment(value).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z),
        attemptTwo: attempt2 ? moment(attempt2).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
        breakTime: selectedBreakTime?.value ? Number(selectedBreakTime?.value) : NUMBER_0,
        note,
        error: !!checkInErrorMessage || !!checkOutErrorMessage,
      });
    }
  };

  // Handle no show contact attempt2 change handler.
  const handleNoShowAttempt2Change = (value: Date | null) => {
    if (value) {
      setAttempt2(value);
    }
    if (caDetails?.caId && selectedShow && onNoShowChange) {
      onNoShowChange?.({
        caId: Number(caDetails.caId),
        noShowStatus: Number(selectedShow.value),
        noShowHour: NUMBER_0.toString(),
        checkInTime: null,
        checkOutTime: null,
        attemptOne: attempt1 ? moment(attempt1).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null,
        attemptTwo: moment(value).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z),
        breakTime: selectedBreakTime?.value ? Number(selectedBreakTime?.value) : NUMBER_0,
        note,
        error: !!checkInErrorMessage || !!checkOutErrorMessage,
      });
    }
  };

  // Get datetime formatted for checkin, checkout, attempt1 and attempt2.
  const getDateTimeFormatted = (dateTime: any, condition: boolean) => (condition && dateTime ? moment(dateTime).format(DATE_FORMAT_YYYY_MM_DDTHH_MM_SS_000Z) : null);

  // Handle comments input field changes
  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setNote(value);
    if (!(caDetails?.caId && selectedShow && onNoShowChange)) return;
    const showValue = Number(selectedShow.value);
    const hourDiff = calculateHourDifference(checkInTime, checkOutTime);
    const caWorkHours = calculateHoursBasedonMinutes(Number(hourDiff), Number(selectedBreakTime?.value), OperationTypes.Subtract);
    const payload = {
      caId: Number(caDetails.caId),
      noShowStatus: showValue,
      noShowHour: Number(selectedShow?.value) !== NoShow.No_Show ? caWorkHours.toString() : NUMBER_0.toString(),
      checkInTime: getDateTimeFormatted(checkInTime, Number(selectedShow?.value) !== NoShow.No_Show),
      checkOutTime: getDateTimeFormatted(checkOutTime, Number(selectedShow?.value) !== NoShow.No_Show),
      attemptOne: getDateTimeFormatted(attempt1, Number(selectedShow?.value) === NoShow.No_Show && attempt1 !== null),
      attemptTwo: getDateTimeFormatted(attempt2, Number(selectedShow?.value) === NoShow.No_Show && attempt2 !== null),
      breakTime: selectedBreakTime?.value ? Number(selectedBreakTime.value) : NUMBER_0,
      note: value,
      error: !!checkInErrorMessage || !!checkOutErrorMessage,
    };
    onNoShowChange(payload);
  };

  // Method to determine on-call and mandatory OT status
  const getOnCallAndMandatoryOTStatus = (callOutType: number | undefined) => {
    let isOnCall = false;
    let isMandatoryOT = false;
    const type = callOutType ?? NUMBER_0;
    switch (type) {
      case NUMBER_3:
        isOnCall = true;
        break;
      case NUMBER_4:
        isMandatoryOT = true;
        break;
      default:
        break;
    }

    return { isOnCall, isMandatoryOT };
  };

  // Get the on-call and mandatory OT status.
  const { isOnCall, isMandatoryOT } = getOnCallAndMandatoryOTStatus(shiftDetails?.callOutType);

  // Render CA work hours.
  const renderCAWorkHours = () => {
    if (isFrom !== NavigationToEmployeeCardFrom.CA_Assign_Page && isFrom !== NavigationToEmployeeCardFrom.Callout_Page) return null;
    const hours =
      isFrom === NavigationToEmployeeCardFrom.Callout_Page || preferredType === Number(CAListTypes.PREFERRED_CA_LIST)
        ? caDetails?.workHours ?? NUMBER_0
        : caDetails?.actualHours ?? NUMBER_0;
    return (
      <div className="fw-sb">
        {Strings.Schedule.WorkHours}: {hours >= NUMBER_0 ? parseFloat(hours.toFixed(2)) : NUMBER_0} Hrs
      </div>
    );
  };

  // Render employee card header data.
  const renderEmployeeCardHeader = () => (
    <Card.Header className={`d-flex justify-content-between align-items-center ${isCollapsed ? 'collapse' : ''}`}>
      <Col className="d-flex align-items-center">
        {(isFrom === NavigationToEmployeeCardFrom.CA_Assign_Page && roleId === NUMBER_2) || isFrom === NavigationToEmployeeCardFrom.Callout_Page ? (
          <div className="me-2 position-relative z-1">
            <KKICheckbox
              id={`checkbox-${caDetails?.caId.toString()}`}
              name="kkicheckbox"
              checkBoxType="checkbox"
              checked={isSelectAllChecked || selectedEmployees?.find((employee) => employee?.caId === caDetails?.caId) !== undefined}
              onChange={handleCheckboxChange}
            />
          </div>
        ) : (
          ''
        )}
        {isFrom === NavigationToEmployeeCardFrom.Shift_Details_Page && (
          <div className="shift-details-header">
            <span className={`openslot me-1 ${ColorGroups[caDetails?.colorGroup].toLowerCase()}`} />
          </div>
        )}
        {caDetails?.firstName || caDetails?.lastName ? `${caDetails?.firstName ?? ''} ${caDetails?.lastName ?? ''}`.trim() : null}
        {isFrom === NavigationToEmployeeCardFrom.Shift_Details_Page && !caDetails?.onCallReplaced && !caDetails?.mandatoryReplaced && caDetails?.replacement && (
          <span className="replacementca text-mediumred">{Strings.Label.ReplaceCALabel}</span>
        )}
        {isFrom === NavigationToEmployeeCardFrom.Shift_Details_Page && caDetails?.onCallReplaced && (
          <span className="replacementca text-oncallreplacement ">{Strings.Label.OnCallReplacementLabel}</span>
        )}
        {isFrom === NavigationToEmployeeCardFrom.Shift_Details_Page && caDetails?.mandatoryReplaced && (
          <span className="replacementca text-mandatory ">{Strings.Label.MandatoryReplacementLabel}</span>
        )}
      </Col>
      <div className="d-flex">
        {isFrom === NavigationToEmployeeCardFrom.Shift_Details_Page && caDetails?.showOnCallReplace && (
          <Button variant="link" onClick={() => onOnCallReplace?.(caDetails?.caId)} className="me-2 fw-md fs-14">
            {Strings.Button.Replace}
          </Button>
        )}
        {isFrom === NavigationToEmployeeCardFrom.Shift_Details_Page && !caDetails?.showOnCallReplace && caDetails?.onCallReplaced && (
          <Button variant="link" onClick={() => onUndoReplace?.(caDetails?.caId)} className="me-2 fw-md fs-14">
            {Strings.Button.UndoReplace}
          </Button>
        )}
        <Col xs="auto" className="d-flex cardheader-right align-items-center">
          {isFrom === NavigationToEmployeeCardFrom.Shift_Details_Page && (shiftDetails?.onCallRequired ?? NUMBER_0) > NUMBER_0 ? (
            <div className="ms-2 position-relative z-1 fw-sb">
              <KKICheckbox
                id={caDetails?.caId.toString()}
                name="kkicheckbox"
                checkBoxType="checkbox"
                label="On Call"
                disabled={caDetails?.onCallReplaced || caDetails?.mandatoryReplaced}
                hideCursor={caDetails?.onCallReplaced || caDetails?.mandatoryReplaced}
                checked={onCallValues}
                {...(!caDetails?.onCallReplaced && !caDetails?.mandatoryReplaced && { onChange: handleOnCallChange })}
              />
            </div>
          ) : (
            ''
          )}
          {renderCAWorkHours()}
        </Col>
        <CustomToggle eventKey="0" callback={toggleCollapse}>
          <Arrow />
        </CustomToggle>
      </div>
    </Card.Header>
  );

  // Render employee card action buttons.
  const renderEmployeeCardActionButton = () => (
    <Col xs={12} md="auto" className={isFrom !== NUMBER_1 ? 'd-none' : 'text-end'}>
      <Button
        variant="outline-primary"
        type="submit"
        onClick={() => navigate({ pathname: `${AdminRoutePath.SCHViewSchedules}/${caDetails.caId}` }, { state: { prevUrl: location.pathname } })}
      >
        {Strings.Button.ViewSchedules}
      </Button>
      <Button variant="primary" type="submit" onClick={() => onButtonClick?.(caDetails?.caId, MyScheduleActions.Unassign)}>
        {Strings.Button.UnAssign}
      </Button>

      {shiftDetails?.published && isCurrentTimeIsBeforeShiftStartTime(shiftDetails?.shiftToTime || '') ? (
        <Button variant="primary" type="submit" onClick={() => onButtonClick?.(caDetails?.caId, MyScheduleActions.Callout)}>
          {Strings.Shift.Button.CallOut}
        </Button>
      ) : (
        ''
      )}
    </Col>
  );

  // Method to render CA shift information when redirecting from callout list page.
  const renderCallOutCAInfo = () => (
    <>
      <Col xs={6} lg={6} xl={6} xxl={4} className={`mt-min-xxl-16 pt-min-xxl-16 bt-min-xxl ${isFrom === NavigationToEmployeeCardFrom.Callout_Page ? 'card-cell-mob' : ''}`}>
        <span className="lg-ps-2">{Strings.Label.OnCall}</span>
        <span>{isOnCall ? Strings.Label.Yes : Strings.Label.No}</span>
      </Col>
      <Col xs={6} lg={6} xl={6} xxl={4} className="mt-min-xxl-16 pt-min-xxl-16 bt-min-xxl">
        <span className="lg-ps-2">{Strings.Label.MandatoryOT}</span>
        <span>{isMandatoryOT ? Strings.Label.Yes : Strings.Label.No}</span>
      </Col>
      <Col xs={6} lg={6} xl={6} xxl={4} className="mt-min-xxl-16 pt-min-xxl-16 bt-min-xxl">
        <span className="lg-ps-2">{Strings.CAKeyIndicators.OnPremise}</span>
        <span>{caDetails?.onPrem ? Strings.Label.Yes : Strings.Label.No}</span>
      </Col>
    </>
  );

  // Get Accordion ClassName.
  const getAccordionClassName = () => {
    let accClassName = '';
    if (caDetails?.onCallReplaced) {
      accClassName = 'oncall-duty';
    } else if (caDetails?.mandatoryReplaced) {
      accClassName = 'mandatory-duty';
    }
    return accClassName;
  };

  return (
    <Accordion activeKey={!isCollapsed ? '0' : null} className={`shiftcard-main ${getAccordionClassName()}`}>
      <Card className={isFrom === NavigationToEmployeeCardFrom.CA_Assign_Page && caDetails?.assignedHours > NUMBER_40 ? 'time-exceed overflow-visible' : 'overflow-visible'}>
        {renderEmployeeCardHeader()}
        <Accordion.Collapse eventKey="0">
          <Card.Body className="shift-details">
            <Col className="shift-details-sub">
              <Row className="mb-3">
                <Col xs={6} lg={6} xl={6} xxl={4} className="mb-3">
                  {Strings.Label.EmpId}: <span>{caDetails?.empId}</span>
                </Col>
                {isFrom !== NavigationToEmployeeCardFrom.Shift_Details_Page && (
                  <Col xs={6} lg={6} xl={6} xxl={4} className="card-cell-mob">
                    <span className="lg-ps-2">{Strings.Label.ColorGroup}</span>
                    <span>{ColorGroups[caDetails?.colorGroup]}</span>
                  </Col>
                )}
                <Col xs={6} lg={6} xl={6} xxl={4} className={isFrom === NavigationToEmployeeCardFrom.Callout_Page ? 'card-cell-mob' : ''}>
                  <span className="lg-ps-2">{Strings.Label.Contact}</span>
                  <span>{caDetails?.phone ? formatPhoneNumber(caDetails?.phone) : ''}</span>
                </Col>
                {isFrom === NavigationToEmployeeCardFrom.Callout_Page && renderCallOutCAInfo()}

                {shiftDetails &&
                  shiftDetails?.published &&
                  isFrom === NavigationToEmployeeCardFrom.Shift_Details_Page &&
                  !isCurrentTimeIsBeforeShiftStartTime(shiftDetails?.shiftFromTime || '') && (
                    <>
                      <Col className="fw-normal mb-3">
                        <KKISelect
                          id="Status"
                          label={Strings.Label.AttendanceType}
                          name="Status"
                          className="custom-select"
                          placeholder={Strings.Label.AttendanceType}
                          searchvalue={false}
                          options={caDetails?.isNoShow || NUMBER_0 > NoShow.Select ? show.filter((item) => item.value !== NUMBER_0.toString()) : show}
                          value={selectedShow}
                          onSelectChange={handleNoShowStatusChange}
                        />
                      </Col>
                      {Number(selectedShow?.value) !== NoShow.No_Show && (
                        <>
                          <Col xl={4} className="date-time mb-3">
                            <label htmlFor="timing" className="form-label fw-normal">
                              {Strings.Label.CheckInTime}
                              <input type="hidden" id="timing" />
                            </label>
                            <DateTimePicker
                              id="checkInTime"
                              calendarIcon={
                                <Button variant="Link" onClick={(e) => e.stopPropagation()}>
                                  <Time />
                                </Button>
                              }
                              disableClock
                              value={checkInTime}
                              onChange={handleCheckInChange}
                              disabled={Number(selectedShow?.value) === NoShow.Select}
                              yearPlaceholder="yyyy"
                              monthPlaceholder="mm"
                              dayPlaceholder="dd"
                              hourPlaceholder="hh"
                              minutePlaceholder="mm"
                            />
                            {checkInErrorMessage && <span className="error">{checkInErrorMessage}</span>}
                          </Col>

                          <Col className="date-time mb-3">
                            <label htmlFor="timing" className="form-label fw-normal">
                              {Strings.Label.CheckOutTime}
                              <input type="hidden" id="timing" />
                            </label>
                            <DateTimePicker
                              id="checkOutTime"
                              calendarIcon={
                                <Button variant="Link" onClick={(e) => e.stopPropagation()}>
                                  <Time />
                                </Button>
                              }
                              disableClock
                              value={checkOutTime}
                              onChange={handleCheckOutChange}
                              disabled={Number(selectedShow?.value) === NoShow.Select}
                              yearPlaceholder="yyyy"
                              monthPlaceholder="mm"
                              dayPlaceholder="dd"
                              hourPlaceholder="hh"
                              minutePlaceholder="mm"
                            />
                            {checkOutErrorMessage && <span className="error">{checkOutErrorMessage}</span>}
                          </Col>

                          <Col className="fw-normal">
                            <KKISelect
                              id="breaktime"
                              label="Break Time"
                              name="breaktime"
                              className="custom-select"
                              placeholder={Strings.Label.BreakTime}
                              searchvalue={false}
                              options={breakTimeOptions}
                              value={selectedBreakTime}
                              disabled={Number(selectedShow?.value) === NoShow.Select}
                              onSelectChange={handleBreakTimeChange}
                            />
                          </Col>
                        </>
                      )}
                      {Number(selectedShow?.value) === NoShow.No_Show && (
                        <Col className="date-time">
                          <label htmlFor="timing" className="form-label fw-normal">
                            {Strings.Label.AttemptOne}
                            <input type="hidden" id="timing" />
                          </label>
                          <DateTimePicker
                            id="checkInTime"
                            calendarIcon={
                              <Button variant="Link" onClick={(e) => e.stopPropagation()}>
                                <Time />
                              </Button>
                            }
                            disableClock
                            value={attempt1}
                            onChange={handleNoShowAttempt1Change}
                            yearPlaceholder="yyyy"
                            monthPlaceholder="mm"
                            dayPlaceholder="dd"
                            hourPlaceholder="hh"
                            minutePlaceholder="mm"
                          />
                        </Col>
                      )}
                      {Number(selectedShow?.value) === NoShow.No_Show && (
                        <Col className="date-time mb-3">
                          <label htmlFor="timing" className="form-label fw-normal">
                            {Strings.Label.AttemptTwo}
                            <input type="hidden" id="timing" />
                          </label>
                          <DateTimePicker
                            id="checkOutTime"
                            calendarIcon={
                              <Button variant="Link" onClick={(e) => e.stopPropagation()}>
                                <Time />
                              </Button>
                            }
                            disableClock
                            value={attempt2}
                            onChange={handleNoShowAttempt2Change}
                            yearPlaceholder="yyyy"
                            monthPlaceholder="mm"
                            dayPlaceholder="dd"
                            hourPlaceholder="hh"
                            minutePlaceholder="mm"
                          />
                        </Col>
                      )}

                      <Col sm={12} className="fw-normal">
                        <KKIInput
                          id="note"
                          label={Strings.Label.Note}
                          name="lastName"
                          type="text"
                          as="textarea"
                          rows={NUMBER_2}
                          value={note}
                          onChange={onInputChange}
                          maxLength={NUMBER_300}
                          disabled={Number(selectedShow?.value) === NoShow.Select}
                        />
                      </Col>
                    </>
                  )}
              </Row>
            </Col>
            <Row className="align-items-center">
              <Col xs={12} md>
                {Strings.Label.KeyIndicators}:
                <span className="fw-sb">
                  {' '}
                  {getKeyIndicators({
                    floatStaff: caDetails?.floatStaff,
                    fullTime: caDetails?.fullTime,
                    lighDuty: caDetails?.lightDuty,
                    volunteer: caDetails?.volunteer,
                    student: caDetails?.student,
                    restrictedOt: caDetails?.restrictedOt,
                    partTime16: caDetails?.partTime16,
                    partTime20: caDetails?.partTime20,
                    partTime24: caDetails?.partTime24,
                  })}
                </span>
              </Col>
              {renderEmployeeCardActionButton()}
            </Row>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
};
export default EmployeeCard;
